import { t } from '@/i18n';
import Big from 'big.js';
import { FALLBACK_TEXT } from './constants';
import { BigCalculator } from './number-calculator';
import {
  type AmountUnit,
  AmountUnitLabelModifier,
  AmountUnitMultipliers,
} from './schemas';

/**
 * 数値を日本語ロケール形式でフォーマットする内部関数
 * -0を0に変換する処理を含む
 */
const intlNumberFormat = (
  value: number | bigint,
  options?: Intl.NumberFormatOptions | undefined,
): string => {
  return new Intl.NumberFormat('ja-JP', options).format(
    //NOTE: Converting -0 to 0 by adding 0 to value
    typeof value === 'number' ? value + 0 : value,
  );
};

type NegativeSignOptions = {
  useNegativeSign?: boolean;
};
type FormatNumberOptions = Pick<
  Intl.NumberFormatOptions,
  'minimumFractionDigits' | 'maximumFractionDigits' | 'useGrouping'
> &
  NegativeSignOptions;

/**
 * 数値を指定されたオプションに基づいてフォーマットする関数
 * null/undefinedの場合はFALLBACK_TEXT、負の値が含まれる集計値で0の場合は特別な処理を行う
 * useNegativeSignオプションで負の値を△記号で表示可能
 */
export const formatNumber = (
  value: number | bigint | null | undefined,
  options?: FormatNumberOptions,
) => {
  if (value === null || value === undefined) {
    return FALLBACK_TEXT;
  }

  const { isAggregateValueZero, fallbackValue } =
    handleZeroValueWithNegativeCount(value, options);
  if (isAggregateValueZero) {
    return fallbackValue;
  }

  const formattedValue = intlNumberFormat(value, {
    maximumFractionDigits: options?.maximumFractionDigits,
    minimumFractionDigits: options?.minimumFractionDigits,
    useGrouping: options?.useGrouping,
  });

  return options?.useNegativeSign
    ? formatNegativeValue(formattedValue)
    : formattedValue;
};

type Operation = 'round' | 'floor' | 'ceil';
export type FormatValueByUnitOptions = {
  operation?: Operation;
  unit?: AmountUnit;
  showUnitLabel?: boolean;
  enableSeparator?: boolean;
  negativeCount?: number | null;
  isDebugMode?: boolean;
} & NegativeSignOptions;

/**
 * 数値に対して指定された端数処理（round/floor/ceil）を実行する関数
 * operationが指定されていない場合は元の値をそのまま返す
 */
export const numberOperator = (
  value: number,
  operation?: FormatValueByUnitOptions['operation'],
): number => {
  if (!operation) {
    return value;
  }
  return operation === 'ceil' || operation === 'floor'
    ? Math[operation](value)
    : Big(value).round().toNumber();
};

/**
 * プラスマイナスが相殺されて0になった値を特別に処理する関数
 * negativeCountが存在し、値が0の場合は集計値として特別な表示を返す
 * デバッグモードでは開発者向けメッセージを表示
 */
export const handleZeroValueWithNegativeCount = (
  value: number | bigint | null | undefined,
  options?: FormatValueByUnitOptions,
):
  | {
      isAggregateValueZero: true;
      fallbackValue: string;
    }
  | {
      isAggregateValueZero: false;
      fallbackValue: number | null;
    } => {
  const hasNegativeValue = options?.negativeCount && options.negativeCount > 0;
  const isDebugMode = options?.isDebugMode;
  if (hasNegativeValue && value === 0) {
    return {
      isAggregateValueZero: true,
      fallbackValue: isDebugMode
        ? t('dev-tools.aggregate-value-is-zero')
        : FALLBACK_TEXT,
    };
  }
  return {
    isAggregateValueZero: false,
    fallbackValue:
      typeof value === 'number' || typeof value === 'bigint'
        ? Number(value)
        : null,
  };
};

/**
 * 金額を指定された単位（plain/thousand/million）で表示用にフォーマットする関数
 * 単位変換、端数処理、桁区切り、単位ラベル表示などの機能を提供
 * 元の値が0でないのに単位変換後に0になる場合の特別処理も含む
 */
export const formatValueByUnit = (
  value: number | null | undefined,
  options?: FormatValueByUnitOptions,
): string => {
  if (typeof value !== 'number') {
    return FALLBACK_TEXT;
  }

  const displayValue: Record<AmountUnit, number> = {
    plain: value,
    thousand: numberOperator(
      formatAmountNumberByUnit(value, 'thousand'),
      options?.operation,
    ),
    million: numberOperator(
      formatAmountNumberByUnit(value, 'million'),
      options?.operation,
    ),
  };
  const unit = options?.unit ?? 'plain';
  const valueByUnit = displayValue[unit];

  /* 負の値の数が存在し、単位変換後の値が0だが、元の値が0でない場合は'0'を返す
    例: 1000円を千円単位で表示する場合、100 -> 0.1 -> 0（四捨五入）となり、
    元の値が0でないのに表示が--になってしまうため、特別な処理が必要
  */
  if (
    typeof options?.negativeCount === 'number' &&
    valueByUnit === 0 &&
    displayValue.plain !== 0
  ) {
    return '0';
  }

  const formattedValue = formatNumber(valueByUnit, {
    useNegativeSign: true,
    useGrouping: options?.enableSeparator,
    ...options,
  });
  return options?.showUnitLabel
    ? `${formattedValue} ${AmountUnitLabelModifier[unit]}`
    : formattedValue;
};

/**
 * マイナス記号（-）を三角記号（△）に置換する関数
 * 日本の会計慣習に従った負の値の表示形式
 */
export const formatNegativeValue = (value: string): string => {
  return value.replace('-', '△');
};

/**
 * 金額を指定された単位で除算して数値形式に変換する関数
 * 例: 1000円を千円単位に変換 → 1
 */
export const formatAmountNumberByUnit = (
  value: number,
  unit: AmountUnit,
): number => {
  return BigCalculator.divide(value, AmountUnitMultipliers[unit]) ?? 0;
};

type FormatPercentageOptions = FormatNumberOptions & {
  hideUnit?: boolean;
};

/**
 * 数値をパーセンテージ形式でフォーマットする関数
 * 0.1 → 10%、hideUnitオプションで%記号を非表示可能
 * useNegativeSignオプションで負の値を△記号で表示
 */
export const formatPercentage = (
  value: number | null | undefined,
  options?: FormatPercentageOptions,
) => {
  if (typeof value !== 'number') {
    return FALLBACK_TEXT;
  }

  let percentage = '';

  percentage = intlNumberFormat(value, {
    style: 'percent',
    maximumFractionDigits: options?.maximumFractionDigits ?? 2,
    minimumFractionDigits: options?.minimumFractionDigits,
  });

  if (options?.hideUnit) {
    percentage = percentage.replace('%', '');
  }

  if (options?.useNegativeSign) {
    percentage = formatNegativeValue(percentage);
  }

  return percentage;
};

/**
 * 分数（分子/分母）をパーセンテージ形式でフォーマットする関数
 * 例: 分子=1、分母=4 → 25%
 * 内部でBigCalculatorを使用して精度の高い除算を実行
 */
export const formatFraction = (
  numerator: number | null | undefined,
  denominator: number | null | undefined,
  options?: FormatPercentageOptions,
) => {
  if (typeof numerator !== 'number' || typeof denominator !== 'number') {
    return FALLBACK_TEXT;
  }

  const result = BigCalculator.divide(numerator, denominator);

  return formatPercentage(result, options);
};

/**
 * 数値を負数に変換する関数。主に合計額の計算のために使用する。
 * - 入力が数値でない場合は 0 を返す
 * - 0 の場合は 0 を返す
 * - 正数の場合は -1 を掛けて負数に変換
 * - 負数の場合は正の値を返す
 */
export const convertToNegative = (
  value: number | null | undefined,
): number | null => {
  if (value === null || value === undefined) {
    return null;
  }
  if (value === 0) {
    return 0;
  }
  return value * -1;
};

/**
 * 金額を指定された単位に変換する関数
 * @param value 変換する金額
 * @param unit 変換後の単位（'plain' | 'thousand' | 'million'）
 * @param operation 端数処理の方法（'round' | 'floor' | 'ceil'）
 * @returns 変換後の金額
 */
export const convertAmountByUnit = ({
  value,
  unit = 'plain',
  operation,
}: {
  value: number | null | undefined;
  unit?: AmountUnit;
  operation?: Operation;
}): number => {
  if (typeof value !== 'number') {
    return 0;
  }

  const convertedValue = formatAmountNumberByUnit(value, unit);
  return numberOperator(convertedValue, operation);
};

/**
 * 合計値と構成要素の値を検証し、適切な値を返す
 * - 構成要素が相殺されて0になる場合はnullを返す
 * - 構成要素にnull値が含まれる場合はnullを返す
 *
 * @returns
 * - 'isZero': 0-打ち消しなどの場合
 * - 'isNull': 打ち消しの場合
 * - 'skip': 純粋なnull、純粋な数値、100円以下の計算の場合。
 */
export const validateTotalWithElements = ({
  totalValue,
  elements,
}: {
  totalValue: number | null | undefined;
  elements: {
    value: number | null | undefined;
    rawValue: number | null | undefined;
    negativeCount: number | null | undefined;
  }[];
}): {
  result: 'isZero' | 'isNull' | 'skip';
} => {
  // この関数は0/null時の判定ロジックなので、それ以外の場合はskip
  if (typeof totalValue !== 'number' || totalValue) {
    return { result: 'skip' };
  }

  const pureValueAllZero = elements.every(({ value }) => value === 0);

  const calculateValueZero =
    elements.reduce<null | number>((result, current) => {
      if (typeof current.value !== 'number') {
        return result;
      }
      // 親で差引き分はマイナス変換しているため、ここではaddしている。
      return BigCalculator.add(result ?? 0, current.value);
    }, null) === 0;

  const calculateRawZero =
    elements.reduce<null | number>((result, current) => {
      if (typeof current.rawValue !== 'number') {
        return result;
      }
      // 親で差引き分はマイナス変換しているため、ここではaddしている。
      return BigCalculator.add(result ?? 0, current.rawValue);
    }, null) === 0;

  const hasNegativeCount = elements.some(
    ({ negativeCount, value }) => !!negativeCount || (value ?? 0) < 0,
  );

  // // 各要素の負の値カウントを考慮した処理結果を取得
  const aggregateResults = elements.map((element) => {
    return handleZeroValueWithNegativeCount(element.value, {
      negativeCount: element.negativeCount,
    });
  });

  // 表示上は０だが入力値が存在する場合の制御
  if (calculateValueZero && elements.some((v) => v.rawValue)) {
    return { result: calculateRawZero ? 'isNull' : 'isZero' };
  }

  if (
    pureValueAllZero &&
    calculateValueZero &&
    calculateRawZero &&
    hasNegativeCount
  ) {
    const allAggregateResultsIsZero = aggregateResults.every(
      ({ isAggregateValueZero }) => isAggregateValueZero,
    );
    return { result: allAggregateResultsIsZero ? 'isNull' : 'isZero' };
  }

  if (pureValueAllZero && calculateRawZero) {
    return { result: 'isZero' };
  }

  // 計算結果が0の場合。
  if (calculateValueZero && calculateRawZero) {
    const hasZero = aggregateResults.some(
      ({ fallbackValue }) => fallbackValue === 0,
    );

    // 0を直接入力の場合はisZeroとなる。
    return {
      result: hasNegativeCount && !hasZero ? 'isNull' : 'isZero',
    };
  }

  return { result: 'skip' };
};
